import CONFIG from "../../../core/consts/config";
import { NEXAR_PC_USER_ID, NEXAR_USER_ID } from "./consts";
import { convertPaymentDetails, convertSubscription } from "../utils";
import {
  PaymentDetails,
  PCResponse,
  PCSubscription,
  Subscription,
} from "../../../contracts/nexar-api/types";
import MD5 from "crypto-js/md5";
import { NexarApiContract } from "../../../contracts/nexar-api/nexar-api";
import {
  fetchRxdaemonAuthenticated,
  getOrUpdatePersonalCloudAccessToken,
  getOrUpdateRxdaemonAccessToken,
  postPcAuthenticated,
  validateOtpCodeAndLogin as authValidateOtpCodeAndLogin,
  logout as authLogout,
} from "./auth";
import { getValue } from "./storage";
import { string } from "yup";

async function sendOtpCode(countryCode: string, phoneNumber: string) {
  const clientId = new Date().getTime().toString();
  const phoneWithCountryCode = encodeURIComponent(countryCode) + phoneNumber;
  const crypted = MD5(phoneWithCountryCode);

  const headers = {
    Authorization: `Nexar ${crypted} FUTURE_SIGNATURE_NOT_IMPLEMENTED_YET`,
    "Content-Type": "application/json",
  };

  await fetch(
    `${CONFIG.AUTH_SERVER}/sms-request?client_id=${clientId}&phone_number=${phoneWithCountryCode}`,
    { headers }
  );
  return { clientId };
}

async function getUserData() {
  const userId = getRxdaemonUserId();
  let data;
  try {
    data = await fetchRxdaemonAuthenticated("GET", `/api/user/${userId}`);
  } catch (err) {
    throw err;
  }
  if (data.error) {
    throw new Error(data.error);
  } else {
    return data;
  }
}

async function validateOtpCodeAndLogin(
  countryCode: string,
  phoneNumber: string,
  digits: string,
  clientId: string
): Promise<void> {
  return authValidateOtpCodeAndLogin(
    countryCode,
    phoneNumber,
    digits,
    clientId
  );
}

async function logout() {
  return authLogout();
}

const getSubscriptionsWithModules = async (): Promise<{
  data: Subscription[] | null;
}> => {
  const personalCloudUserId = getValue(NEXAR_PC_USER_ID);
  const data = { user_id: { id: personalCloudUserId } };

  const newEndpointPromise = postPcAuthenticated(
    `/get-subscriptions-by-user-id`,
    data
  );
  const oldEndpointPromise = postPcAuthenticated(
    `/get-user-subscriptions`,
    data
  );

  // The order matters, the new endpoint takes precedence
  const apiResults = await Promise.allSettled<{ success: any; payload: any }>([
    newEndpointPromise,
    oldEndpointPromise,
  ]);

  if (apiResults.some((promise) => promise.status === "fulfilled")) {
    // A map of subscriptions to avoid duplicates
    const unifiedSubscriptions: { [key: string]: Subscription } = {};

    // After all the promises settle, focus only on the successful ones
    apiResults.forEach((promise) => {
      if (promise.status === "fulfilled") {
        const { success, payload } = promise.value;
        if (success) {
          payload?.subscriptions.forEach((sub: PCSubscription) => {
            // If already imported, ignore
            if (typeof unifiedSubscriptions[sub.id.id] !== "undefined") {
              return;
            }
            if (sub.cancellation_cause === "ORDER_RETURN") {
              return;
            }
            if (!!sub.internal_id) {
              return;
            }
            if (!sub.device_sns || sub.device_sns.length === 0) {
              return;
            }
            unifiedSubscriptions[sub.id.id] = convertSubscription(sub);
          });
        }
      }
    });

    return { data: Object.values(unifiedSubscriptions) || null };
  }
  return { data: null };
};

const getSubscriptionById = async ({
  subscriptionId,
}: {
  subscriptionId: string;
}): Promise<{ data: Subscription }> => {
  const data = { id: { id: subscriptionId } };

  const { success, payload } = await postPcAuthenticated(
    `/get-subscriptions-by-id`,
    data
  );
  if (success) {
    const convertedSubscription = convertSubscription(payload?.subscription);
    return { data: convertedSubscription };
  }
  return { data: payload };
};

const createSubscriptionSetupIntent = async ({
  subscriptionId,
}: {
  subscriptionId: string;
}): Promise<{ clientSecret: string }> => {
  const data = { id: { id: subscriptionId } };

  const { success, payload } = await postPcAuthenticated(
    `/create-subscription-setup-intent`,
    data
  );

  return { clientSecret: payload.client_secret };
};

const activateSubscriptionById = async ({
  subscriptionId,
  paymentDetails,
}: {
  subscriptionId: string;
  paymentDetails: PaymentDetails;
}): Promise<PCResponse> => {
  const data = {
    id: { id: subscriptionId },
    payment_details: convertPaymentDetails(paymentDetails),
  };
  return await postPcAuthenticated(`/activate-subscription`, data);
};

const updateSubscriptionById = async ({
  subscriptionId,
  paymentDetails,
}: {
  subscriptionId: string;
  paymentDetails: PaymentDetails;
}): Promise<PCResponse> => {
  const data = {
    id: { id: subscriptionId },
    payment_details: convertPaymentDetails(paymentDetails),
  };
  return await postPcAuthenticated(
    `/update-subscription-payment-details`,
    data
  );
};

const deleteSubscriptionById = async ({
  subscriptionId,
}: {
  subscriptionId: string;
}): Promise<PCResponse> => {
  const data = {
    id: { id: subscriptionId },
    cancellation_cause: "USER_CANCELLATION",
  };
  return await postPcAuthenticated(`/cancel-subscription`, data);
};
// PERSONAL CLOUD

async function isAuthenticated(): Promise<boolean> {
  const rxdaemonAccessToken = await getOrUpdateRxdaemonAccessToken();
  const personalCloudAccessToken = await getOrUpdatePersonalCloudAccessToken();

  return !!rxdaemonAccessToken && !!personalCloudAccessToken;
}

function getPersonalCloudUserId(): string {
  const personalCloudUserId = getValue(NEXAR_PC_USER_ID);
  if (!personalCloudUserId) {
    throw Error("PC user id not found. Is the user authenticated?");
  }
  return personalCloudUserId;
}

function getRxdaemonUserId(): string {
  const rxdaemonUserId = getValue(NEXAR_USER_ID);
  if (!rxdaemonUserId) {
    throw Error("Rxdaemon user id not found. Is the user authenticated?");
  }
  return rxdaemonUserId;
}

const NexarApi: NexarApiContract = {
  logout,
  getUserData,
  activateSubscriptionById,
  getSubscriptionById,
  getSubscriptionsWithModules,
  updateSubscriptionById,
  deleteSubscriptionById,
  isAuthenticated,
  sendOtpCode,
  validateOtpCodeAndLogin,
  createSubscriptionSetupIntent,
  getPersonalCloudUserId,
  getRxdaemonUserId,
};
export default NexarApi;
